import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { Modal } from '@/components/modal/modal';
import { Button, HStack, Separator, VStack } from '@chakra-ui/react';
import type { GoogleDrivePermission } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import DriveCurrentAccessSection from './DriveCurrentAccessSection';
import DriveDomainShareToggle from './DriveDomainShareToggle';
import DriveExternalEmailsField from './DriveExternalEmailsField';
import DriveTeammatesChecklist from './DriveTeammatesChecklist';
import { useDriveManageAccess } from './use-drive-manage-access';

const I18N_PREFIX = 'integrations.googleDrive.sharing';

interface DriveManageAccessModalProps {
  onClose: () => void;
}

const permissionSubject = (permission: GoogleDrivePermission): string =>
  permission.displayName || permission.emailAddress || permission.domain || '';

const DriveManageAccessModal = ({ onClose }: DriveManageAccessModalProps) => {
  const { t } = useTranslation();
  const access = useDriveManageAccess();

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={t(`${I18N_PREFIX}.manageTitle`)}
        maxWidth="lg"
        isCentered
        footer={
          <HStack justify="flex-end" width="full" gap={2}>
            <Button variant="outline" onClick={onClose} disabled={access.submitInFlight}>
              {t(`${I18N_PREFIX}.invite.cancel`)}
            </Button>
            <Button
              colorPalette="gray"
              onClick={access.submit}
              disabled={!access.canSubmit}
              loading={access.submitInFlight}
            >
              {t(`${I18N_PREFIX}.invite.submit`, { count: access.totalRecipients })}
            </Button>
          </HStack>
        }
      >
        <VStack align="stretch" gap={5}>
          <DriveCurrentAccessSection
            permissions={access.permissions}
            badgeFor={access.badgeFor}
            revokingId={access.revokingId}
            onRevoke={access.requestRevoke}
            isLoading={access.isLoadingPermissions}
            isError={access.isPermissionsError}
          />

          <Separator />

          {access.workspaceDomain && (
            <DriveDomainShareToggle
              domain={access.workspaceDomain}
              checked={access.shareWithDomain}
              onChange={access.setShareWithDomain}
            />
          )}

          <DriveTeammatesChecklist
            teamMembers={access.teamMembers}
            selectedIds={access.selectedMemberIds}
            alreadySharedEmails={access.alreadySharedEmails}
            onToggle={access.toggleMember}
            isLoading={access.isLoadingTeam}
          />

          <DriveExternalEmailsField
            value={access.externalRaw}
            onChange={access.setExternalRaw}
            invalidEmails={access.invalidExternal}
          />
        </VStack>
      </Modal>

      <ConfirmDialog
        isOpen={!!access.pendingRevoke}
        title={t(`${I18N_PREFIX}.revokeConfirm.title`)}
        description={t(`${I18N_PREFIX}.revokeConfirm.description`, {
          subject: access.pendingRevoke ? permissionSubject(access.pendingRevoke) : '',
        })}
        confirmColorPalette="red"
        confirmText={t(`${I18N_PREFIX}.list.revoke`)}
        onConfirm={access.confirmRevoke}
        onCancel={access.cancelRevoke}
        isLoading={access.revokeInFlight}
      />
    </>
  );
};

export default DriveManageAccessModal;
